import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { CaptureFrames } from '@/components/capture/CaptureFrames';
import { rejectionText } from '@/components/jobs/job-text';
import { FieldList } from '@/components/review/FieldList';
import { QualityCardPanel } from '@/components/review/QualityCardPanel';
import { RefinePanel } from '@/components/review/RefinePanel';
import { OperatorFooter, OutcomePanel, ReviewerFooter, type FooterOutcome } from '@/components/review/ReviewFooter';
import { Sheet } from '@/components/review/Sheet';
import { VoiceNotesPanel } from '@/components/review/VoiceNotesPanel';
import {
  CONFIRMABLE_STATUSES,
  ESCALATABLE_STATUSES,
  FINISHED_STATUSES,
  FRAME_CHANGE_STATUSES,
  MAX_FRAMES,
  RERUNNABLE_STATUSES,
  buildAttributes,
  buildConfirmRequest,
  missingRequiredLabels,
  qualityChipLabel,
  showsReviewerFooter,
  statusLabel,
  isWorking,
  productHeadline,
} from '@/components/review/review-model';
import { useCaptureRecord } from '@/components/review/useCaptureRecord';
import {
  changeFrame,
  confirmCapture,
  escalateCapture,
  rerunCapture,
  submitReviewVerdict,
  uploadCaptureAsset,
} from '@/lib/api';
import type { CorrectionReason, FrameChange, QualityCard, RerunRequest } from '@/lib/api/types';
import { useAuth } from '@/lib/auth/auth-context';
import { useMyAccess } from '@/lib/access/access-context';
import { setBusy as markUnsaved } from '@/lib/busy';
import { workflowTitle } from '@/lib/capture-workflows';
import { sha256Fingerprint } from '@/lib/fingerprint';
import { downscaleForUpload } from '@/lib/downscale-image';
import { cn } from '@/lib/utils';

/**
 * CA-05, the shared review surface (ADR-006, INTERFACES.md 15.2), laid out
 * as wireframe R-A (plan/smart-capture/wireframes/mobile-fit.html). The
 * operator lands here seconds after the shutter, and the manager lands here
 * from the console's "Review and fix". Both see one screen that never
 * scrolls as a page: the title, the grade chip and the status; the frame
 * strip with Add and, beside it, every required field that came back
 * empty; the Fields, Refine and Notes tabs, of which only the open panel
 * scrolls; and the actions pinned at the bottom, chosen by role. The tab
 * bar is hidden on this route (`isTaskRoute`), and the grade chip opens the
 * quality breakdown in a sheet.
 *
 * Every attribute renders, read or empty. An empty one is an input, and the
 * required ones still empty sit at the top, because an operator standing at
 * the item can fill a missing model number in seconds, and a reviewer
 * working from a photograph hours later cannot.
 *
 * Controls stay available after a confirm. Once the capture is approved,
 * rejected or staged the review routes answer 409, so the actions disable
 * and the status chip says why. A rejected capture names its reason beside
 * the status, with Auto when a rule rejected it (INTERFACES.md section 3).
 *
 * An unsaved field edit marks the screen busy (`lib/busy.ts`, key
 * `result`), so a new deployment waits for the confirm rather than
 * reloading the edit away.
 *
 * The screen polls while the pipeline works on the capture (the first read,
 * or a re-read after a retake or a rerun) and while a voice note is out for
 * transcription. QUARANTINED stops the poll: the capture will not change on
 * its own, so the screen offers a retake instead.
 */

type ResultTab = 'fields' | 'refine' | 'notes';

const GRADE_TONE: Record<QualityCard['grade'], string> = {
  A: 'border-success text-success',
  B: 'border-success text-success',
  C: 'border-warning text-warning',
  D: 'border-warning text-warning',
  F: 'border-danger text-danger',
};

function messageOf(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

export function ExtractionResult() {
  const { captureId } = useParams<{ captureId: string }>();
  const { user } = useAuth();
  const { access } = useMyAccess();
  const { record, error: loadError, stalled, reload, watch } = useCaptureRecord(captureId);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, CorrectionReason>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<FooterOutcome | null>(null);
  const [framesChanged, setFramesChanged] = useState(false);
  const [tab, setTab] = useState<ResultTab>('fields');
  const [qualityOpen, setQualityOpen] = useState(false);

  const reviewer = showsReviewerFooter(access.permissions.reviewCaptures, user?.sub, record?.capturedBy);
  const attributes = useMemo(
    () => (record ? buildAttributes(record, record.attention) : []),
    [record],
  );
  const request = buildConfirmRequest(attributes, drafts, reasons);
  const unsaved = Boolean(request.fieldEdits || request.fieldAdds);

  useEffect(() => {
    markUnsaved('result', unsaved);
  }, [unsaved]);
  useEffect(() => () => markUnsaved('result', false), []);

  if (loadError && !record) {
    return <p className="text-sm text-warning">{loadError}</p>;
  }
  if (!record || !captureId) {
    return <p className="text-sm text-muted">Loading capture...</p>;
  }

  const assetKeys = record.assetKeys ?? [];
  const working = isWorking(record);
  const quarantined = record.status === 'QUARANTINED';
  const finished = FINISHED_STATUSES.includes(record.status);
  const pendingChanges =
    Object.keys(request.fieldEdits ?? {}).length + Object.keys(request.fieldAdds ?? {}).length;
  const headline = productHeadline(record);
  const retakenBefore = (record.iterations ?? []).some(
    (iteration) => iteration.trigger === 'RETAKE' || iteration.trigger === 'ADD_FRAME',
  );
  const chip = quarantined ? null : qualityChipLabel(record.qualityCard, record.confidence);
  const rejection = record.status === 'REJECTED' ? record.rejection : undefined;
  const noteCount = (record.voiceNotes ?? []).length || (record.voiceTranscript ? 1 : 0);
  const tabs: { id: ResultTab; label: string; count?: number }[] = quarantined
    ? [{ id: 'notes', label: 'Notes', count: noteCount }]
    : [
        { id: 'fields', label: 'Fields', count: attributes.length },
        { id: 'refine', label: 'Refine' },
        { id: 'notes', label: 'Notes', count: noteCount },
      ];
  const shownTab = tabs.some((item) => item.id === tab) ? tab : tabs[0].id;
  const backTo = record.captureJobId ? `/jobs/${encodeURIComponent(record.captureJobId)}` : '/work';

  function setDraft(name: string, value: string | undefined) {
    setDrafts((current) => {
      const next = { ...current };
      if (value === undefined) delete next[name];
      else next[name] = value;
      return next;
    });
  }

  function setReason(name: string, reason: CorrectionReason | undefined) {
    setReasons((current) => {
      const next = { ...current };
      if (reason === undefined) delete next[name];
      else next[name] = reason;
      return next;
    });
  }

  function clearDrafts() {
    setDrafts({});
    setReasons({});
  }

  async function run<T>(action: () => Promise<T>, fallback: string): Promise<T | null> {
    setBusy(true);
    setActionError(null);
    try {
      return await action();
    } catch (cause: unknown) {
      setActionError(messageOf(cause, fallback));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function sendFrame(file: File, frame: Pick<FrameChange, 'action' | 'index'>) {
    if (!captureId) return;
    const captureJobId = record?.captureJobId;
    const accepted = await run(async () => {
      if (!captureJobId) {
        throw new Error('This capture belongs to no capture job, so a photograph cannot be added.');
      }
      const photo = await downscaleForUpload(file);
      const fingerprint = await sha256Fingerprint(photo);
      const { assetKey } = await uploadCaptureAsset({
        mode: 'photo',
        fingerprint,
        bytes: photo,
        contentType: photo.type || 'image/jpeg',
        captureJobId,
      });
      return changeFrame(captureId, { ...frame, assetKey, fingerprint });
    }, 'Could not send the photograph');
    if (accepted) {
      setFramesChanged(true);
      setOutcome(null);
      watch(accepted.status);
    }
  }

  async function rerun(body: RerunRequest): Promise<boolean> {
    if (!captureId) return false;
    const accepted = await run(() => rerunCapture(captureId, body), 'Could not start Rerun AI');
    if (!accepted) return false;
    setOutcome(null);
    setTab('fields');
    watch(accepted.status);
    return true;
  }

  async function confirm() {
    if (!captureId) return;
    const response = await run(() => confirmCapture(captureId, request), 'Could not save your confirmation');
    if (!response) return;
    clearDrafts();
    setOutcome({ kind: 'confirmed', response });
    await reload();
  }

  async function escalate(question: string): Promise<boolean> {
    if (!captureId) return false;
    const response = await run(() => escalateCapture(captureId, question), 'Could not send the question');
    if (!response) return false;
    setOutcome({ kind: 'escalated' });
    await reload();
    return true;
  }

  /*
    A reviewer's corrections go in before the verdict. Values the extraction
    read go through the verdict route's own "edit", which is the reviewer
    learning signal. Values nobody read cannot be edited there, because the
    route refuses a field the record does not hold, so they go through the
    confirm route, which accepts additions from a reviewer.
  */
  async function verdict(kind: 'approve' | 'reject' | 'request_recapture', reason?: string): Promise<boolean> {
    if (!captureId) return false;
    const response = await run(async () => {
      if (kind === 'approve' && request.fieldAdds) {
        await confirmCapture(captureId, {
          fieldAdds: request.fieldAdds,
          ...(request.reasons ? { reasons: request.reasons } : {}),
        });
      }
      if (kind === 'approve' && request.fieldEdits) {
        await submitReviewVerdict(captureId, { verdict: 'edit', fieldEdits: request.fieldEdits });
      }
      return submitReviewVerdict(captureId, { verdict: kind, ...(reason ? { reason } : {}) });
    }, 'Could not record the verdict');
    if (!response) return false;
    if (kind === 'approve') clearDrafts();
    setOutcome({ kind: 'verdict', verdict: kind, status: response.status });
    await reload();
    return true;
  }

  const canChangeFrames = FRAME_CHANGE_STATUSES.includes(record.status) && !busy;
  const showActions = reviewer || !quarantined;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <header className="flex flex-none items-start gap-1">
        {/* Back to the job the capture belongs to, which is where the
            operator came from. A capture taken before jobs has none. */}
        <Link
          to={backTo}
          aria-label={record.captureJobId ? 'Back to the job' : 'Back to my jobs'}
          className="-ml-3 flex size-11 shrink-0 items-center justify-center rounded-control text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <ChevronLeft className="size-5" aria-hidden="true" />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted">
            {record.workflow ? workflowTitle(record.workflow) : 'Product'}
          </p>
          <h1 className="line-clamp-2 break-words text-base font-semibold leading-snug">{headline.title}</h1>
          <div className="flex flex-wrap items-center gap-1.5">
            {chip && (
              <button
                type="button"
                onClick={() => setQualityOpen(true)}
                aria-haspopup="dialog"
                aria-label={`Quality ${chip}`}
                className={cn(
                  'inline-flex min-h-7 items-center rounded-badge border px-2 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                  record.qualityCard ? GRADE_TONE[record.qualityCard.grade] : 'border-border-strong text-surface-foreground',
                )}
              >
                {chip}
              </button>
            )}
            <Badge tone={quarantined || rejection ? 'danger' : finished ? 'success' : 'neutral'} className="gap-1.5">
              {working && <Loader2 className="size-3 animate-spin" aria-hidden="true" />}
              {working && stalled ? `Still ${statusLabel(record.status).toLowerCase()}` : statusLabel(record.status)}
            </Badge>
            {rejection && <Badge tone="danger">{rejectionText(rejection)}</Badge>}
            {reviewer && <Badge>Reviewing</Badge>}
          </div>
          {rejection?.detail && (
            <p className="truncate text-xs text-muted" title={rejection.detail}>
              &ldquo;{rejection.detail}&rdquo;
            </p>
          )}
        </div>
      </header>

      {quarantined && (
        <p className="flex-none text-sm text-danger" role="alert">
          Retake a frame to read it again.
        </p>
      )}

      <section aria-label="Source frames" className="flex-none">
        <CaptureFrames
          captureId={captureId}
          assetKeys={assetKeys}
          maxFrames={MAX_FRAMES}
          busy={!canChangeFrames}
          preferServer={framesChanged || retakenBefore}
          missing={working || quarantined ? [] : missingRequiredLabels(record.attention)}
          onRetake={(index, file) => void sendFrame(file, { action: 'REPLACE', index })}
          onAddFrame={(file) => void sendFrame(file, { action: 'ADD' })}
        />
      </section>

      <div role="tablist" aria-label="Result" className="flex flex-none border-b border-border-strong">
        {tabs.map(({ id, label, count }) => {
          const active = shownTab === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              id={`result-tab-${id}`}
              aria-selected={active}
              aria-controls={`result-panel-${id}`}
              onClick={() => setTab(id)}
              className={cn(
                'flex min-h-11 flex-1 items-center justify-center gap-1.5 border-b-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                active ? 'border-accent font-semibold text-surface-foreground' : 'border-transparent text-muted',
              )}
            >
              {label}
              {count !== undefined && count > 0 && <span className="text-xs text-muted">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* The one part of the screen that scrolls. */}
      <div
        role="tabpanel"
        id={`result-panel-${shownTab}`}
        aria-labelledby={`result-tab-${shownTab}`}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
      >
        {shownTab === 'fields' && (
          <FieldList
            attributes={attributes}
            assetKeys={assetKeys}
            drafts={drafts}
            reasons={reasons}
            disabled={busy || working || finished}
            onDraft={setDraft}
            onReason={setReason}
          />
        )}
        {shownTab === 'refine' && (
          <RefinePanel
            captureId={captureId}
            attributes={attributes}
            iterations={record.iterations ?? []}
            canRerun={RERUNNABLE_STATUSES.includes(record.status)}
            busy={busy || working}
            onRerun={rerun}
            onVoiceRecorded={() => watch()}
          />
        )}
        {shownTab === 'notes' && <VoiceNotesPanel record={record} onRecorded={() => watch()} />}
      </div>

      {(actionError || outcome || showActions) && (
        <footer
          className="-mx-4 flex flex-none flex-col gap-2 border-t border-border-strong px-4 pt-2.5 sm:-mx-6 sm:px-6"
          style={{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom))' }}
        >
          {actionError && (
            <p className="text-sm text-warning" role="alert">
              {actionError}
            </p>
          )}
          {outcome && (
            <OutcomePanel
              outcome={outcome}
              nextTo={record.captureJobId ? `/jobs/${encodeURIComponent(record.captureJobId)}/capture` : '/capture'}
            />
          )}
          {reviewer ? (
            <ReviewerFooter
              busy={busy}
              pendingChanges={pendingChanges}
              canDecide={!working && !finished}
              onVerdict={verdict}
            />
          ) : (
            !quarantined && (
              <OperatorFooter
                busy={busy}
                canConfirm={CONFIRMABLE_STATUSES.includes(record.status)}
                canEscalate={ESCALATABLE_STATUSES.includes(record.status)}
                onConfirm={() => void confirm()}
                onEscalate={escalate}
              />
            )
          )}
        </footer>
      )}

      <Sheet open={qualityOpen} title="Quality" onClose={() => setQualityOpen(false)}>
        <QualityCardPanel card={record.qualityCard} confidence={record.confidence} />
      </Sheet>
    </div>
  );
}
